import { gql } from "@apollo/client";
import { Platform } from "react-native";
import { EligibilityStatus, SubmissionStatus } from "./types";

export const MEMBER_SETTINGS_FRAGMENT = gql`
  fragment MemberSettingsFragment on MembershipMemberSettings {
    activityReviewOnboarding
    mobileAppOnboarding
    deferredCompOnboarding
    planThresholdMet
    acknowledgedPlanChangeAt
  }
`;

export const CURRENT_MEMBER_FRAGMENT = gql`
  fragment CurrentMemberFragment on MembershipMember {
    id: memberNumber
    firstName
    lastName
    memberType
    memberClass
    enrolled: deferredCompEnrolled
    eligibilityStatus: deferredCompEligibilityStatus
    primaryEmail
    intercomHmac {${Platform.OS}}
    externalId
    memberSettings {
      ...MemberSettingsFragment
    }
  }
  ${MEMBER_SETTINGS_FRAGMENT}
`;

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFragment on CRMAddress {
    id
    address1
    address2
    city
    state
    postalCode
    countryCode
    isVerified
    addressType
  }
`;

export interface CurrentPerson {
  id: string;
  dateOfBirth: string;
}

export const CURRENT_PERSON_FRAGMENT = gql`
  fragment CurrentPersonFragment on CRMPerson {
    id
    contactAddress {
      ...AddressFragment
      addressable {
        ... on CRMFacility {
          id
          name
        }
      }
    }
    addresses {
      ...AddressFragment
    }
    pendingChangeRequests {
      id
      status
      createdAt
      details
      facility {
        id
        name
      }
    }
    phoneNumbers {
      id
      phoneNumber
      phoneType
      phoneCountry
      phoneFormatted
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export interface MemberSettings {
  activityReviewOnboarding: boolean;
  mobileAppOnboarding: boolean;
  deferredCompOnboarding: boolean;
  planThresholdMet: boolean;
  acknowledgedPlanChangeAt: string;
}

export interface CurrentMember {
  id: string;
  firstName: string;
  lastName: string;
  memberType: string;
  enrolled: boolean;
  eligibilityStatus: EligibilityStatus;
  primaryEmail?: string;
  intercomHmac?: { [key in typeof Platform.OS]?: string };
  externalId?: string;
  memberSettings?: MemberSettings;
  memberClass?: string;
}

export const ACTIVITY_FRAGMENT = gql`
  fragment ActivityFragment on DeferredCompActivity {
    id
    name
    maxPoints
    code
    imageUrl
    description
    tags {
      id: name
      name
    }
    participationLevels {
      id
      requirements
      points
    }
  }
`;

export interface Plan {
  activities: Activity[];
  startsOn: string;
  submissionWindowClosesOn?: string | null;
  minimumPointsThreshold: number;
  confirmedPoints: number;
  id: string;
}

export interface Activity {
  tags: { name: string }[];
  name: string;
  description: string;
  maxPoints: number;
  imageUrl?: string;
  id: string;
  code: string;
  participationLevels: ParticipationLevel[];
}

interface ParticipationLevel {
  id: string;
  points: number;
  requirements: string;
}

export const ACTIVITY_DETAILS_FRAGMENT = gql`
  fragment ActivityDetailsFragment on DeferredCompActivity {
    id
    code
    name
    description
    imageUrl
    participationLevels {
      id
      requirements
      points
    }
  }
`;

export const MY_ACTIVITY_FRAGMENT = gql`
  fragment MyActivityFragment on DeferredCompSubmission {
    id
    status
    expiresSoon
    expirationDate
    participationLevel {
      id
      points
      activity {
        id
        name
      }
    }
  }
`;

export const MY_ACTIVITY_DETAILS_FRAGMENT = gql`
  fragment MyActivityDetailsFragment on DeferredCompSubmission {
    id
    completionDate
    expirationDate
    reviewRequestDate
    createdAt
    expiresSoon
    statusChangedAt
    status
    comments
    participationLevel {
      id
      activity {
        name
        description
        tags {
          name
          id: name
        }
      }
      points
      requirements
    }
    reviewer {
      id
      name
      firstName
      lastName
      profilePhoto
    }
    rejectionMessage
    rejectionReason
  }
`;

export const EMPLOYMENTS_FRAGMENT = gql`
  fragment EmploymentsFragment on CRMPerson {
    employments {
      id
      title
      isPrimary
      startedOn
      description
      jobClassification
      workingLocation
      facility {
        id
        name
        addresses {
          ...AddressFragment
        }
        section {
          id
          name
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export interface MyActivity {
  id: string;
  status: SubmissionStatus;
  expiresSoon: boolean;
  expirationDate: string;
  participationLevel: {
    id: string;
    points: number;
    activity: {
      id: string;
      name: string;
    };
  };
}

export const PDR_HISTORY_FRAGMENT = gql`
  fragment PDRHistoryFragment on MembershipPdrHistory {
    activityCode
    category
    creditType
    creditsAccepted
    creditsAppliedFor
    description1
    description2
    id
    location
    participationDates {
      startedOn
      endedOn
    }
    submittedOn
  }
`;

export interface PdrHistory {
  activityCode: string;
  category: string;
  creditType: string;
  creditsAccepted: number;
  creditsAppliedFor: number;
  description1: string;
  description2: string;
  id: string;
  location: string;
  participationDates: {
    startedOn: string;
    endedOn: string;
  };
  submittedOn: string;
}
